import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { mockCategories } from '@/data/mockData';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { History, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useSegmentSelection } from '@/hooks/useSegmentSelection';
import { useVolumeEstimation } from '@/hooks/useVolumeEstimation';
import { SegmentSelectionPanel } from '@/components/SegmentSelectionPanel';
import { VolumeGrid } from '@/components/VolumeGrid';
import { OrderForm } from '@/components/OrderForm';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usersApi, clientsApi, requestsApi, API_BASE_URL, getBasicAuthHeader, fetchCrmPopulations } from '@/lib/apiClient';
import { mapApiSegmentsToCategories } from '@/lib/mapApiSegmentsToCategories';
import {
  mapApiEstimationToSegmentVolumes,
  findPopulationVolumes,
  extractTotalsFromAll,
  extractPopulationTotals,
  ApiEstimationVolumes,
  ApiPopulationVolumes,
} from '@/lib/mapApiEstimationToVolumes';
import { PopulationVolumeView, VolumeTotals } from '@/components/VolumeGrid';
import { PopulationFormState, PopulationFormEntry } from '@/components/OrderForm';
import { DEFAULT_OUTPUT_FORMAT, OutputFormat } from '@/constants/outputFormat';

interface ApiSegment {
  name: string;
  type: string;
  name_data?: string;
}

interface ApiClientWithSegments {
  id: number;
  name: string;
  available_segments?: Record<string, ApiSegment[]>;
  country?: string;
  cpm_price?: string;  // Format: "15.00"
  minimum_price?: number;  // Format: 200
}

const Configuration = () => {
  const [duration, setDuration] = useState<'30' | '60' | '90'>('30');
  const [requestedVolume, setRequestedVolume] = useState<string>('');
  const [contactType, setContactType] = useState<string>('');
  const [outputFormat, setOutputFormat] = useState<OutputFormat>(DEFAULT_OUTPUT_FORMAT);
  const [requestName, setRequestName] = useState<string>('');
  const [showEstimate, setShowEstimate] = useState(false);
  const [selectedCrm, setSelectedCrm] = useState<string>('');
  const [selectedCrmExclusion, setSelectedCrmExclusion] = useState<string[]>([]);
  const [showDeliveryDialog, setShowDeliveryDialog] = useState(false);
  const [isEstimating, setIsEstimating] = useState(false);
  const [isCreatingRequest, setIsCreatingRequest] = useState(false);
  const [estimationVolumes, setEstimationVolumes] = useState<ApiEstimationVolumes | undefined>(undefined);
  // --- Feature population ---
  const [populationVolumes, setPopulationVolumes] = useState<ApiPopulationVolumes | undefined>(undefined);
  const [populationForm, setPopulationForm] = useState<PopulationFormState>({});
  const [apiTotals, setApiTotals] = useState<{
    email: number;
    mobile: number;
    emailOrMobile: number;
    emailAndMobile: number;
  } | null>(null);
  const { user, selectedClient, setClient } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isSettingVolumesRef = useRef(false);

  // Récupérer l'utilisateur courant pour obtenir son ID
  const { data: currentUser, isLoading: isLoadingCurrentUser, isFetching: isFetchingCurrentUser } = useQuery({
    queryKey: ['current-user', user?.email],
    queryFn: async () => {
      if (!user?.email) {
        throw new Error('Utilisateur non connecté');
      }
      const userData = await usersApi.getUserByEmailUsersByEmailEmailGet(user.email);
      if (!userData || typeof userData.id !== 'number') {
        throw new Error("Utilisateur non trouvé pour cet email");
      }
      return userData;
    },
    enabled: !!user?.email,
  });

  // Récupérer la liste des clients de l'utilisateur connecté
  const { data: userClients, isLoading: isLoadingUserClients, isFetching: isFetchingUserClients } = useQuery({
    queryKey: ['user-clients', currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) {
        throw new Error('Utilisateur non disponible');
      }
      const clientsResponse = await usersApi.getUserClientsUsersUserIdClientsGet(currentUser.id);
      return Array.isArray(clientsResponse) ? (clientsResponse as ApiClientWithSegments[]) : [];
    },
    enabled: !!currentUser?.id,
  });

  const currentClient = useMemo(() => {
    if (!selectedClient || !Array.isArray(userClients)) {
      return null;
    }
    return (userClients as ApiClientWithSegments[]).find(
      (client) => client.name === selectedClient
    ) || null;
  }, [selectedClient, userClients]);

  // Vérifier si un client est sélectionné, sinon rediriger vers la sélection
  // Attendre que la réhydratation depuis localStorage soit terminée
  useEffect(() => {
    // Attendre que les données soient chargées et que l'auth soit prête
    if (isLoadingCurrentUser || isLoadingUserClients) {
      return;
    }

    // Vérifier aussi dans localStorage au cas où la réhydratation n'a pas encore eu lieu
    const storedClient = typeof window !== 'undefined' 
      ? window.localStorage.getItem('enrichment_app_selected_client')
      : null;

    // Si pas de client sélectionné (ni en mémoire ni en localStorage), rediriger vers la sélection
    if (!selectedClient && !storedClient) {
      navigate('/client-selection');
      return;
    }
  }, [selectedClient, isLoadingCurrentUser, isLoadingUserClients, navigate]);

  // Si le client stocké n'existe plus (ou n'est plus accessible), nettoyer et renvoyer vers la sélection
  // Attendre que les queries soient terminées avant de valider le client
  useEffect(() => {
    if (!selectedClient) {
      return;
    }

    // Ne pas valider si les queries sont en cours de chargement ou de rechargement
    if (isLoadingCurrentUser || isFetchingCurrentUser || isLoadingUserClients || isFetchingUserClients) {
      return;
    }

    // Attendre que userClients soit chargé (pas undefined)
    if (!Array.isArray(userClients)) {
      return;
    }

    // Vérifier si le client est valide seulement après que toutes les données soient chargées
    if (!currentClient) {
      setClient('');
        toast({
          variant: 'destructive',
          title: 'Client non disponible',
          description: "Le client sélectionné précédemment n'est plus accessible. Veuillez en choisir un autre.",
        });
      navigate('/client-selection');
    }
  }, [selectedClient, userClients, currentClient, isLoadingCurrentUser, isFetchingCurrentUser, isLoadingUserClients, isFetchingUserClients, setClient, toast, navigate]);

  const baseCategories = useMemo(() => {
    if (!currentClient || !currentClient.available_segments) {
      return [] as typeof mockCategories;
    }
    return mapApiSegmentsToCategories(currentClient.available_segments, mockCategories);
  }, [currentClient]);

  const categories = useMemo(
    () => mapApiEstimationToSegmentVolumes(baseCategories, estimationVolumes),
    [baseCategories, estimationVolumes]
  );

  // Récupérer les fichiers CRM pour le client courant
  const { data: crmFiles, isLoading: isLoadingCrmFiles, error: crmFilesError } = useQuery({
    queryKey: ['client-crm-files', currentClient?.id],
    queryFn: async () => {
      if (!currentClient?.id) {
        return [] as string[];
      }
      const response = await clientsApi.listClientCrmFilesClientsClientIdCrmFilesGet(currentClient.id);
      return Array.isArray(response) ? (response as string[]) : [];
    },
    enabled: !!currentClient?.id,
  });

  // Détecter la présence de la colonne `population` pour le CRM sélectionné
  // (cf. contract.md §1). Rétro-compatible : sans population -> has_population=false.
  const crmPopulationsQueryKey = ['crm-populations', currentClient?.id, selectedCrm];
  const crmPopulationsQueryFn = async () => {
    if (!currentClient?.id || !selectedCrm) {
      return { has_population: false, populations: [] as string[] };
    }
    return fetchCrmPopulations(currentClient.id, selectedCrm);
  };
  const { data: crmPopulations } = useQuery({
    queryKey: crmPopulationsQueryKey,
    queryFn: crmPopulationsQueryFn,
    enabled: !!currentClient?.id && !!selectedCrm,
  });

  const hasPopulation = Boolean(crmPopulations?.has_population) && (crmPopulations?.populations.length ?? 0) > 0;
  const populations = useMemo(
    () => (hasPopulation ? crmPopulations?.populations ?? [] : []),
    [hasPopulation, crmPopulations]
  );

  // Extract segment selection logic to custom hook
  const {
    selectedSegments,
    handleSegmentToggle,
    handleCategoryToggle,
    isSingleSegmentInCategory,
    selectedSegmentsByCategory,
    selectedSegmentDetails,
    singleSelectedSegment,
  } = useSegmentSelection(categories);

  // Extraire et convertir les valeurs CPM et prix plancher du client
  const clientCpmPrice = useMemo(() => {
    if (!currentClient?.cpm_price) return undefined;
    const parsed = parseFloat(currentClient.cpm_price);
    return isNaN(parsed) ? undefined : parsed;
  }, [currentClient?.cpm_price]);

  const clientMinimumPrice = useMemo(() => {
    return currentClient?.minimum_price;
  }, [currentClient?.minimum_price]);

  // En mode population, les populations activées (avec type de contact + volume).
  const activePopulationEntries = useMemo<{ population: string; entry: PopulationFormEntry }[]>(() => {
    if (!hasPopulation) {
      return [];
    }
    return populations
      .map((population) => ({ population, entry: populationForm[population] }))
      .filter((p): p is { population: string; entry: PopulationFormEntry } =>
        Boolean(p.entry?.active)
      );
  }, [hasPopulation, populations, populationForm]);

  // Volume facturé = somme des volumes demandés par population activée (cf. contract.md §3).
  const populationTotalVolume = useMemo(() => {
    return activePopulationEntries.reduce((sum, { entry }) => {
      return sum + (parseInt(entry.requestedVolume.replace(/\s/g, ''), 10) || 0);
    }, 0);
  }, [activePopulationEntries]);

  // Volume transmis au calcul de prix : en mode population -> somme ; sinon volume saisi.
  // Le prix plancher reste appliqué UNE SEULE FOIS sur le total (logique useVolumeEstimation).
  const effectiveRequestedVolume = hasPopulation
    ? String(populationTotalVolume)
    : requestedVolume;

  // Extract volume estimation and validation logic to custom hook
  const {
    estimatedTotals: calculatedTotals,
    totalPrice,
    validateVolume,
    areButtonsEnabled,
    estimateCalculated,
    handleEstimate: handleEstimateFromHook,
    resetEstimate,
  } = useVolumeEstimation({
    selectedSegmentDetails,
    singleSelectedSegment,
    isSingleSegmentInCategory,
    duration,
    requestedVolume: effectiveRequestedVolume,
    contactType,
    requestName,
    selectedSegments,
    cpmPrice: clientCpmPrice,
    minimumPrice: clientMinimumPrice,
    apiTotals,  // Passer les totaux de l'API pour une validation précise
  });

  // Activation des boutons en mode population : nom + ≥1 population activée complète.
  const populationButtonsEnabled = useMemo(() => {
    if (!hasPopulation) {
      return false;
    }
    if (!requestName || activePopulationEntries.length === 0) {
      return false;
    }
    return activePopulationEntries.every(({ entry }) => {
      const vol = parseInt(entry.requestedVolume.replace(/\s/g, ''), 10) || 0;
      return Boolean(entry.contactType) && vol > 0;
    });
  }, [hasPopulation, requestName, activePopulationEntries]);

  // Mise à jour d'une entrée du formulaire population.
  const handlePopulationEntryChange = (population: string, patch: Partial<PopulationFormEntry>) => {
    setPopulationForm((prev) => {
      const current: PopulationFormEntry = prev[population] ?? {
        active: false,
        contactType: '',
        requestedVolume: '',
      };
      return { ...prev, [population]: { ...current, ...patch } };
    });
  };

  // Vues par population pour la grille de volumes (onglet "Par population").
  // 1 entrée = 1 population. Le volume est UN TOTAL agrégé par population
  // (union dédupliquée tous segments confondus, calculé côté API), pas un détail
  // par segment (cf. contract.md §2). Fallback à 0 si la population est absente
  // de `population_volumes`.
  const populationViews = useMemo<PopulationVolumeView[]>(() => {
    if (!hasPopulation) {
      return [];
    }
    return populations.map((population) => {
      const totals =
        extractPopulationTotals(findPopulationVolumes(populationVolumes, population)) ?? {
          email: 0,
          mobile: 0,
          emailOrMobile: 0,
          emailAndMobile: 0,
        };
      return { population, totals };
    });
  }, [hasPopulation, populations, populationVolumes]);

  // Map population -> totaux estimés, transmis à OrderForm pour le cap par population.
  const populationTotals = useMemo<Record<string, VolumeTotals>>(() => {
    const map: Record<string, VolumeTotals> = {};
    populationViews.forEach((view) => {
      map[view.population] = view.totals;
    });
    return map;
  }, [populationViews]);

  // Cap par population : au moins une population activée dépasse son volume estimé
  // (uniquement une fois l'estimation lancée, sinon on ne connaît pas le max).
  const hasPopulationOverCap = useMemo(() => {
    if (!hasPopulation || !estimateCalculated) {
      return false;
    }
    return activePopulationEntries.some(({ population, entry }) => {
      const totals = populationTotals[population];
      if (!totals || !entry.contactType) {
        return false;
      }
      let max = 0;
      if (entry.contactType === 'email') max = totals.email;
      else if (entry.contactType === 'mobile') max = totals.mobile;
      else if (entry.contactType === 'email_or_mobile') max = totals.emailOrMobile;
      else if (entry.contactType === 'email_and_mobile') max = totals.emailAndMobile;
      const requested = parseInt(entry.requestedVolume.replace(/\s/g, ''), 10);
      return !isNaN(requested) && requested > max;
    });
  }, [hasPopulation, estimateCalculated, activePopulationEntries, populationTotals]);

  // Activation effective : en mode population, on bloque aussi si un dépassement existe.
  const effectiveButtonsEnabled = hasPopulation
    ? populationButtonsEnabled && !hasPopulationOverCap
    : areButtonsEnabled;

  // Utiliser les totaux de l'API ("All") si disponibles, sinon les totaux calculés
  const estimatedTotals = apiTotals || calculatedTotals;

  // Reset estimation when selection changes (but not when we're setting volumes from API)
  useEffect(() => {
    // Ne pas réinitialiser si on vient juste de définir les volumes depuis l'API
    if (isSettingVolumesRef.current) {
      isSettingVolumesRef.current = false;
      return;
    }
    // Note: estimateCalculated is managed by useVolumeEstimation hook
    // We only need to reset showEstimate UI state here
    setShowEstimate(false);
    setApiTotals(null);
    setEstimationVolumes(undefined);
    setPopulationVolumes(undefined);
  }, [selectedSegments]);

  // Réinitialiser le formulaire population quand l'ensemble des populations change
  // (changement de CRM, donc valeurs distinctes différentes).
  useEffect(() => {
    setPopulationForm({});
    setPopulationVolumes(undefined);
    // L'estimation devient obsolète : repasser à "—" (estimateCalculated=false).
    resetEstimate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCrm]);

  // Si le CRM source est dans les exclusions, le retirer automatiquement
  useEffect(() => {
    if (selectedCrm && selectedCrmExclusion.includes(selectedCrm)) {
      setSelectedCrmExclusion((prev) => prev.filter((f) => f !== selectedCrm));
    }
  }, [selectedCrm, selectedCrmExclusion]);

  // Reset estimation when CRM, exclusions, or duration changes
  useEffect(() => {
    setShowEstimate(false);
    setApiTotals(null);
    setEstimationVolumes(undefined);
    setPopulationVolumes(undefined);
    // estimateCalculated doit aussi redevenir false (sinon le tableau affiche 0 au lieu de "—").
    resetEstimate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCrm, selectedCrmExclusion, duration]);

  // Libellé FR d'un type de contact pour l'affichage récapitulatif.
  const contactTypeLabel = (type: string): string => {
    switch (type) {
      case 'email':
        return 'Emails';
      case 'mobile':
        return 'Mobiles';
      case 'email_or_mobile':
        return 'Emails ou Mobiles';
      case 'email_and_mobile':
        return 'Emails et Mobiles';
      default:
        return 'CRM IDs';
    }
  };

  // Mapper contactType vers le format id_type de l'API
  const mapContactTypeToIdType = (contactType: string): string => {
    switch (contactType) {
      case 'email':
        return 'emails';
      case 'mobile':
        return 'phones';
      case 'email_or_mobile':
        return 'emails OR phones';
      case 'email_and_mobile':
        return 'emails AND phones';
      default:
        return 'emails';
    }
  };

  // Construire la structure selected_segments pour l'API à partir de la sélection actuelle
  const buildSelectedSegmentsPayload = () => {
    if (!currentClient || !currentClient.available_segments) {
      return {};
    }

    const payload: Record<string, { name: string; type: string; name_data: string }[]> = {};

    selectedSegmentsByCategory.forEach(({ category, segments }) => {
      const apiSegments = currentClient.available_segments?.[category.name] || [];

      const mappedSegments = segments.map((segment) => {
        const apiSegment = apiSegments.find(
          (s) => s.name.toLowerCase() === segment.name.toLowerCase()
        );
        return {
          name: segment.name,
          type: apiSegment?.type || 'unknown',
          name_data: apiSegment?.name_data || segment.name,
        };
      });

      if (mappedSegments.length > 0) {
        payload[category.name] = mappedSegments;
      }
    });

    return payload;
  };

  // Construire le payload complet pour créer une demande via POST /requests/
  const buildRequestPayload = () => {
    if (!currentUser || !currentClient) {
      throw new Error('Utilisateur ou client non disponible');
    }

    const selectedSegmentsPayload = buildSelectedSegmentsPayload();

    const populationMode = hasPopulation && activePopulationEntries.length > 0;

    // Choix par population (cf. contract.md §3). Vide/absent si pas de population.
    const populationRequests = populationMode
      ? activePopulationEntries.map(({ population, entry }) => ({
          population,
          id_type: mapContactTypeToIdType(entry.contactType),
          requested_volume: parseInt(entry.requestedVolume.replace(/\s/g, ''), 10) || 0,
        }))
      : [];

    // Volume facturé : somme des volumes population, sinon volume saisi (rétro-compatible).
    const requestedVolumeNumber = populationMode
      ? populationTotalVolume
      : parseInt(requestedVolume.replace(/\s/g, ''), 10) || 0;

    // id_type de référence (champ existant) : en mode population, on prend celui de
    // la 1re population activée ; l'autorité reste `population_requests`.
    // NB (cf. contract.md §3) : en mode population, les champs top-level `id_type` et
    // `max_volume` sont des champs LEGACY. Le backend les ignore quand
    // `population_requests` est non vide (seul `population_requests` pilote le flux).
    // `id_type` n'est donc qu'un proxy (1re population) et `max_volume` est indicatif,
    // calculé sur ce seul id_type : ni l'un ni l'autre n'a de rôle fonctionnel ici.
    const effectiveContactType = populationMode
      ? activePopulationEntries[0].entry.contactType
      : contactType;

    // Calculer max_volume selon le contactType
    let maxVolume = 0;
    if (effectiveContactType === 'email') {
      maxVolume = estimatedTotals.email;
    } else if (effectiveContactType === 'mobile') {
      maxVolume = estimatedTotals.mobile;
    } else if (effectiveContactType === 'email_or_mobile') {
      maxVolume = estimatedTotals.emailOrMobile;
    } else if (effectiveContactType === 'email_and_mobile') {
      maxVolume = estimatedTotals.emailAndMobile;
    }

    // Calculer final_price_eur avec la formule: cpm * 0.001 * requested_volume, avec prix plancher.
    // Le prix plancher s'applique UNE SEULE FOIS sur le total (somme en mode population).
    const cpm = clientCpmPrice ?? 18; // Valeur par défaut si non disponible
    const minPrice = clientMinimumPrice ?? 450; // Prix plancher par défaut si non disponible
    const rawPrice = cpm * 0.001 * requestedVolumeNumber;
    const finalPriceEur = Math.max(rawPrice, minPrice);

    const payload: Record<string, unknown> = {
      name: requestName,
      user_id: currentUser.id,
      client_id: currentClient.id,
      selected_segments: selectedSegmentsPayload,
      days_back: parseInt(duration, 10),
      requested_volume: requestedVolumeNumber,
      max_volume: maxVolume,
      id_type: mapContactTypeToIdType(effectiveContactType),
      output_format: outputFormat,
      final_price_eur: finalPriceEur,
      crm_file_label: selectedCrm,
      crm_exclusion: selectedCrmExclusion,
    };

    // Rétro-compatibilité : ne joindre `population_requests` que s'il y a des populations.
    if (populationRequests.length > 0) {
      payload.population_requests = populationRequests;
    }

    return payload;
  };

  // Wrapper pour l'appel d'estimation API
  const handleEstimate = async () => {
    if (!currentClient || !user?.email) {
      toast({
        variant: 'destructive',
        title: 'Configuration incomplète',
        description: "Utilisateur ou client non disponible pour l'estimation.",
      });
      return;
    }

    if (!selectedCrm) {
      toast({
        variant: 'destructive',
        title: 'Configuration incomplète',
        description: 'Veuillez sélectionner un fichier CRM avant de lancer une estimation.',
      });
      return;
    }

    if (selectedSegmentsByCategory.length === 0) {
      toast({
        variant: 'destructive',
        title: 'Configuration incomplète',
        description: 'Veuillez sélectionner au moins un segment avant de lancer une estimation.',
      });
      return;
    }

    const selectedSegmentsPayload = buildSelectedSegmentsPayload();

    setIsEstimating(true);
    setShowEstimate(false);

    try {
      const body: any = {
        client_id: currentClient.id,
        client_name: currentClient.name,
        email: user.email,
        country: currentClient.country || 'FR',
        crm_file: selectedCrm,
        selected_segments: selectedSegmentsPayload,
        days_back: parseInt(duration, 10),
        crm_exclusion: selectedCrmExclusion,
      };

      // Si le CRM a une colonne population, demander aussi le détail par population
      // (cf. contract.md §2). Sans population : champ absent -> payload inchangé.
      //
      // On résout les populations via le cache React Query plutôt que via l'état
      // `hasPopulation`/`populations` : au 1er clic sur un CRM, la query
      // `crm-populations` peut ne pas être encore résolue, ce qui omettait
      // `selected_populations` et renvoyait un onglet population à 0 (bug 1re fois).
      // `ensureQueryData` renvoie le cache s'il est frais, sinon attend le fetch.
      const resolvedPopulations = await queryClient.ensureQueryData({
        queryKey: crmPopulationsQueryKey,
        queryFn: crmPopulationsQueryFn,
      });
      const resolvedHasPopulation =
        Boolean(resolvedPopulations?.has_population) &&
        (resolvedPopulations?.populations.length ?? 0) > 0;
      if (resolvedHasPopulation) {
        body.selected_populations = resolvedPopulations.populations;
      }

      const response: any = await (requestsApi as any).getVolumeEstimationRequestsGetVolumeEstimationPost(
        body
      );

      const volumes = response?.estimation_volumes || {};

      // Marquer qu'on est en train de définir les volumes pour éviter que le useEffect ne les efface
      isSettingVolumesRef.current = true;

      setEstimationVolumes(volumes);
      setPopulationVolumes(response?.population_volumes || undefined);

      // Extraire les totaux globaux depuis la catégorie "All" si présente
      // (même helper que pour les totaux par population, sémantique alignée).
      setApiTotals(extractTotalsFromAll(volumes) ?? null);

      handleEstimateFromHook();
      setShowEstimate(true);
    } catch (error) {
      console.error('Erreur lors de la récupération de l’estimation de volumes:', error);
      setEstimationVolumes(undefined);
      setApiTotals(null);
      setShowEstimate(false);
      toast({
        variant: 'destructive',
        title: 'Erreur estimation des volumes',
        description: "Impossible de récupérer l'estimation des volumes. Veuillez réessayer plus tard.",
      });
    } finally {
      setIsEstimating(false);
    }
  };

  // Wrapper for duration change that also resets showEstimate
  const handleDurationChange = (newDuration: '30' | '60' | '90') => {
    setDuration(newDuration);
    setShowEstimate(false);
  };

  // Validation unifiée des champs requis (gère le mode population et le mode standard).
  // Retourne true si la saisie est valide, sinon affiche un toast et retourne false.
  const validateOrderInputs = (): boolean => {
    if (hasPopulation) {
      if (!selectedSegments.length || !requestName) {
        toast({
          variant: 'destructive',
          title: 'Configuration incomplète',
          description: 'Veuillez remplir le nom de la demande et sélectionner des segments.',
        });
        return false;
      }
      if (activePopulationEntries.length === 0) {
        toast({
          variant: 'destructive',
          title: 'Configuration incomplète',
          description: 'Activez au moins une population et renseignez son type de contact et son volume.',
        });
        return false;
      }
      const allValid = activePopulationEntries.every(({ entry }) => {
        const vol = parseInt(entry.requestedVolume.replace(/\s/g, ''), 10) || 0;
        return Boolean(entry.contactType) && vol > 0;
      });
      if (!allValid) {
        toast({
          variant: 'destructive',
          title: 'Volume invalide',
          description: 'Chaque population activée doit avoir un type de contact et un volume supérieur à 0.',
        });
        return false;
      }
      return true;
    }

    if (!selectedSegments.length || !requestedVolume || !contactType || !requestName) {
      toast({
        variant: 'destructive',
        title: 'Configuration incomplète',
        description: 'Veuillez remplir tous les champs requis',
      });
      return false;
    }
    const requestedVolumeNumber = parseInt(requestedVolume.replace(/\s/g, ''), 10) || 0;
    if (requestedVolumeNumber <= 0) {
      toast({
        variant: 'destructive',
        title: 'Volume invalide',
        description: 'Le volume demandé doit être supérieur à 0',
      });
      return false;
    }
    return true;
  };

  const handleGenerateQuote = async () => {
    if (!validateOrderInputs()) {
      return;
    }

    if (!currentUser || !currentClient) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Utilisateur ou client non disponible",
      });
      return;
    }

    setIsCreatingRequest(true);

    try {
      // 1. Créer la demande via POST /requests/
      const payload = buildRequestPayload();
      const createResponse: any = await (requestsApi as any).createRequestRequestsPost(payload);

      // 2. Récupérer le request_id depuis la réponse
      const requestId = createResponse?.id || createResponse?.request_id;
      if (!requestId) {
        throw new Error('ID de demande non trouvé dans la réponse');
      }

      // 3. Mettre à jour le statut à QUOTE_REQUESTED
      const statusUrl = `${API_BASE_URL.replace(/\/$/, '')}/requests/${requestId}/status`;
      const statusResponse = await fetch(statusUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
          Authorization: getBasicAuthHeader(),
        },
        body: JSON.stringify({ status: 'QUOTE_REQUESTED' }),
      });

      if (!statusResponse.ok) {
        console.warn(`Erreur mise à jour statut: ${statusResponse.status}`);
        // On continue quand même car la demande a été créée avec succès
      }

      toast({
        title: "Demande de devis créée",
        description: hasPopulation
          ? `${requestName} - ${populationTotalVolume.toLocaleString('fr-FR')} contacts (par population)`
          : `${requestName} - ${requestedVolume} contacts ${contactType}`,
      });

      navigate('/historique');
    } catch (error) {
      console.error('Erreur lors de la création de la demande:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: "Impossible de créer la demande. Veuillez réessayer plus tard.",
      });
    } finally {
      setIsCreatingRequest(false);
    }
  };

  const handleDeliveryClick = () => {
    if (!validateOrderInputs()) {
      return;
    }

    setShowDeliveryDialog(true);
  };

  const handleConfirmDelivery = async () => {
    if (!validateOrderInputs()) {
      return;
    }

    if (!currentUser || !currentClient) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Utilisateur ou client non disponible",
      });
      return;
    }

    setIsCreatingRequest(true);
    setShowDeliveryDialog(false);

      try {
        // 1. Créer la demande
        const payload = buildRequestPayload();
        const createResponse: any = await (requestsApi as any).createRequestRequestsPost(payload);

        // 2. Récupérer le request_id depuis la réponse
        const requestId = createResponse?.id || createResponse?.request_id;
        if (!requestId) {
          throw new Error('ID de demande non trouvé dans la réponse');
        }

        // 3. Lancer le job via appel HTTP direct (en attendant le support dans le client)
        const launchUrl = `${API_BASE_URL.replace(/\/$/, '')}/requests/${requestId}/launch-job`;
        const launchResponse = await fetch(launchUrl, {
          method: 'POST',
          headers: {
            accept: 'application/json',
            // Authentification Basic depuis les variables d'environnement, alignée avec la config du client API
            Authorization: getBasicAuthHeader(),
          },
        });

        if (!launchResponse.ok) {
          throw new Error(`Erreur lancement job: ${launchResponse.status}`);
        }

      toast({
        title: "Commande créée",
        description: hasPopulation
          ? `${requestName} - ${populationTotalVolume.toLocaleString('fr-FR')} contacts (par population) - ${totalPrice.toFixed(2)}€`
          : `${requestName} - ${requestedVolume} contacts ${contactType} - ${totalPrice.toFixed(2)}€`,
      });
      
      navigate('/historique');
    } catch (error) {
      console.error('Erreur lors de la création de la commande:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: "Impossible de créer la commande. Veuillez réessayer plus tard.",
      });
    } finally {
      setIsCreatingRequest(false);
    }
  };

  // Afficher un loader pendant le chargement initial ou le rechargement des données
  const isLoadingData = isLoadingCurrentUser || isFetchingCurrentUser || isLoadingUserClients || isFetchingUserClients;

  // Si on a un client sélectionné mais que les données sont en cours de chargement, afficher un loader
  if (selectedClient && isLoadingData) {
    return (
      <div className="h-screen bg-background flex flex-col overflow-hidden">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="text-lg text-muted-foreground">Chargement des données du client...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-background flex flex-col overflow-hidden">
      <Navbar />
      
      <div className="flex-1 flex flex-col overflow-hidden max-w-[90%] mx-auto w-full p-6">
        <div className="flex justify-between items-center mb-8 flex-shrink-0">
          <h1 className="text-3xl font-bold text-foreground">Configuration de commande</h1>
          <Button onClick={() => navigate('/historique')} className="flex items-center gap-2">
            <History className="h-4 w-4" />
            Historique des commandes
          </Button>
        </div>
        
        <div className="flex flex-col lg:flex-row gap-6 flex-1 min-h-0 overflow-hidden">
          {/* Colonne gauche : Une seule carte avec 70% */}
          <div className="lg:w-[70%] w-full">
            <Card className="rounded-2xl shadow-md h-full flex flex-col min-h-0">
              <CardHeader className="flex-shrink-0 hidden">
                <CardTitle className="text-lg">Sélection des catégories et segments</CardTitle>
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden min-h-0">
                <div className="flex flex-row gap-6 h-full min-h-0">
                  <SegmentSelectionPanel
                    categories={categories}
                    selectedSegments={selectedSegments}
                    onSegmentToggle={handleSegmentToggle}
                    onCategoryToggle={handleCategoryToggle}
                  />
                  <VolumeGrid
                    selectedCrm={selectedCrm}
                    setSelectedCrm={setSelectedCrm}
                    availableCrmFiles={crmFiles || []}
                    selectedCrmExclusion={selectedCrmExclusion}
                    setSelectedCrmExclusion={setSelectedCrmExclusion}
                    duration={duration}
                    onDurationChange={handleDurationChange}
                    selectedSegmentsByCategory={selectedSegmentsByCategory}
                    estimateCalculated={estimateCalculated}
                    estimatedTotals={estimatedTotals}
                    selectedSegmentDetails={selectedSegmentDetails}
                    isSingleSegmentInCategory={isSingleSegmentInCategory}
                    isEstimating={isEstimating}
                    onEstimate={handleEstimate}
                    hasPopulation={hasPopulation}
                    populationViews={populationViews}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          <OrderForm
            requestName={requestName}
            setRequestName={setRequestName}
            contactType={contactType}
            setContactType={setContactType}
            outputFormat={outputFormat}
            setOutputFormat={setOutputFormat}
            requestedVolume={requestedVolume}
            setRequestedVolume={setRequestedVolume}
            validateVolume={validateVolume}
            totalPrice={totalPrice}
            areButtonsEnabled={effectiveButtonsEnabled}
            onGenerateQuote={handleGenerateQuote}
            onDeliveryClick={handleDeliveryClick}
            cpmPrice={clientCpmPrice}
            minimumPrice={clientMinimumPrice}
            isCreatingRequest={isCreatingRequest}
            hasPopulation={hasPopulation}
            populations={populations}
            populationForm={populationForm}
            onPopulationEntryChange={handlePopulationEntryChange}
            populationTotals={populationTotals}
            estimateCalculated={estimateCalculated}
          />
        </div>
      </div>

      <AlertDialog open={showDeliveryDialog} onOpenChange={setShowDeliveryDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la livraison</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <div className="text-base font-semibold text-foreground">
                Récapitulatif de votre commande:
              </div>
              <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                {hasPopulation && activePopulationEntries.length > 0 ? (
                  <>
                    {activePopulationEntries.map(({ population, entry }) => (
                      <div key={population} className="flex justify-between">
                        <span className="font-medium">{population} :</span>
                        <span>
                          {(parseInt(entry.requestedVolume.replace(/\s/g, ''), 10) || 0).toLocaleString('fr-FR')}{' '}
                          {contactTypeLabel(entry.contactType)}
                        </span>
                      </div>
                    ))}
                    <div className="flex justify-between border-t pt-1">
                      <span className="font-medium">Volume total facturé :</span>
                      <span>{populationTotalVolume.toLocaleString('fr-FR')}</span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between">
                    <span className="font-medium">Volume :</span>
                    <span>
                      {parseInt(requestedVolume.replace(/\s/g, '') || '0').toLocaleString('fr-FR')}{' '}
                      {contactTypeLabel(contactType)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="font-medium">Montant H.T. :</span>
                  <span className="font-bold text-success">{Math.ceil(totalPrice).toLocaleString('fr-FR')} €</span>
                </div>
              </div>
              <div className="text-foreground font-medium pt-3 border-t">
                ⚠️ La confirmation de cette demande entraîne la validation immédiate du devis.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelivery}>
              Confirmer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Configuration;